<template>
  <edit-module
    ref="editModule"
    :saving="saving"
    :disabled="!hasChanges"
    @submit="handleSave"
    @expand="toggleOpen"
  >
    <template #header>
      <div>
        <detail-label :label="headerLabel" :value="technicianDisplayValue" />
      </div>
    </template>
    <template #body>
      <div v-if="open">
        <eligible-technicians-form
          :model-value="formData.userIds"
          :service-category-id="serviceCategoryId"
          :pinned-technician-id="pinnedTechnicianId"
          :pinned-reason="formData.pinnedReason"
          no-save
          @update:model-value="handleTechSelect"
          @update-pinned-reason="handlePinnedReasonUpdate"
        />
        <change-summary :changes="changes" :original="originalValues" />
      </div>
    </template>
  </edit-module>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { getProperty } from "@/lib/helpers.js";

import DetailLabel from "@/components/DetailLabel.vue";
import EditModule from "@/components/EditModule.vue";
import { useMultiTechSelect } from "@/composables/useMultiTechSelect";
import { fullName } from "@/mixins";
import { AppointmentV2 } from "@/typings/appointment";
import ChangeSummary from "../../ChangeSummary.vue";
import EligibleTechniciansForm from "../../forms/EligibleTechniciansForm.vue";

const props = defineProps<{
  appointment: AppointmentV2;
}>();

const store = useStore();

onMounted(() => {
  if (store.state.technicians.length === 0) {
    store.dispatch("fetchTechnicians");
  }
});

const open = ref(false);
const saving = ref(false);

const editModule = ref<InstanceType<typeof EditModule> | null>(null);

const { toTechProfileIds } = useMultiTechSelect();

// --- Initial data helpers ---
// These functions read props.appointment, so they're reactive when
// called inside computed() contexts (headerLabel, changes, etc.)
// and re-evaluate when the appointment prop changes.

function getMultiTechUserIds(): number[] {
  const { appointment_technicians, technicians } = props.appointment;
  if (!appointment_technicians?.length || !technicians?.length) return [];

  const sorted = [...appointment_technicians].sort(
    (a: any, b: any) => a.position - b.position
  );
  return sorted
    .map((at: any) => {
      const profileId = at.technician_profile_id;
      const tech = technicians.find(
        (t: any) =>
          t.technician_profile_id === profileId || t.technician_id === profileId
      );
      return tech?.user_id;
    })
    .filter((id: number | undefined): id is number => id != null);
}

function getInitialUserIds(): number[] {
  const multiTechIds = getMultiTechUserIds();
  if (multiTechIds.length > 0) return multiTechIds;

  const userId = getProperty(props.appointment.technician, "user_id");
  return userId ? [userId] : [];
}

// --- Form state ---

const formData = ref({
  userIds: getInitialUserIds(),
  pinnedReason: props.appointment.pinned_reason || ""
});

const serviceCategoryId = computed(
  () => getProperty(props.appointment.service_category, "id") ?? null
);

const pinnedTechnicianId = computed(
  () => getProperty(props.appointment.technician, "user_id") ?? null
);

// --- Header display ---

const headerLabel = computed(() => {
  const ids = getInitialUserIds();
  return ids.length > 1 ? "Technicians:" : "Technician:";
});

const technicianDisplayValue = computed(() => {
  const ids = getInitialUserIds();
  if (ids.length === 0) return "--";

  const technicians: any[] = store.state.technicians;
  const names = ids
    .map((uid) => {
      const tech = technicians.find((t) => t.user_id === uid);
      return tech ? fullName(tech) : null;
    })
    .filter(Boolean);

  return names.length > 0 ? names.join(", ") : "--";
});

// --- Change detection ---

const originalValues = computed(() => ({
  user_id: getInitialUserIds()
}));

const changes = computed(() => {
  const initial = getInitialUserIds();
  const current = formData.value.userIds;

  if (
    initial.length === current.length &&
    initial.every((id, i) => id === current[i])
  ) {
    return {};
  }

  return { user_id: current };
});

const hasChanges = computed(() => Object.keys(changes.value).length > 0);

// --- Event handlers ---

function handleTechSelect(val: number | number[] | null) {
  if (Array.isArray(val)) {
    formData.value.userIds = val;
  } else {
    formData.value.userIds = val ? [val] : [];
  }
}

function handlePinnedReasonUpdate(reason: string) {
  formData.value.pinnedReason = reason;
}

async function handleSave() {
  const techProfileIds = toTechProfileIds(formData.value.userIds);

  const payload: Record<string, any> = {
    appointmentId: props.appointment.id,
    technician_profile_ids: techProfileIds,
    pinned_reason: formData.value.pinnedReason || undefined
  };

  saving.value = true;
  try {
    await store.dispatch("assignAppointmentEvent", payload);
    await store.dispatch("fetchAppointment", props.appointment.id);
    window.notify.success("Appointment technician updated successfully!");
    editModule.value?.handleClose();
  } catch {
    // Global interceptor handles the error toast
  } finally {
    saving.value = false;
  }
}

function resetForm() {
  formData.value = {
    userIds: getInitialUserIds(),
    pinnedReason: props.appointment.pinned_reason || ""
  };
}

function toggleOpen() {
  open.value = !open.value;
  if (!open.value) {
    resetForm();
  }
}

// Reset form when appointment prop changes
watch(
  () => props.appointment,
  () => resetForm()
);
</script>
